import React, { useState } from 'react';
import { Palette, Info, RotateCcw } from 'lucide-react';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Auto-test simplifié et ORIGINAL de discrimination des couleurs (paires de teintes
// proches sur l'axe rouge-vert, choisies par nous). Ce n'est PAS le test d'Ishihara
// (planches chiffrées protégées) : c'est un repère préliminaire uniquement, pas un
// outil de diagnostic. Un vrai trouble de la vision des couleurs se confirme chez
// un ophtalmologue.
const ROUNDS = [
  { bg: '#8FA377', target: '#A38F6B' },
  { bg: '#C97C68', target: '#7FA36A' },
  { bg: '#6E9B8A', target: '#8A9B6E' },
  { bg: '#B98A6B', target: '#6BA98A' },
  { bg: '#9B7E6E', target: '#7E9B6E' },
  { bg: '#C48A7A', target: '#7AAF8A' },
];

const GRID_SIZE = 9;

const buildGrid = (round) => {
  const oddIndex = Math.floor(Math.random() * GRID_SIZE);
  return { oddIndex, cells: Array.from({ length: GRID_SIZE }, (_, i) => (i === oddIndex ? round.target : round.bg)) };
};

const ColorPerceptionCheck = () => {
  const { t } = useLanguage();
  const [roundIndex, setRoundIndex] = useState(0);
  const [grid, setGrid] = useState(() => buildGrid(ROUNDS[0]));
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handlePick = (i) => {
    if (feedback) return;
    const correct = i === grid.oddIndex;
    if (correct) setScore((s) => s + 1);
    setFeedback(correct ? 'correct' : 'wrong');
    setTimeout(() => {
      setFeedback(null);
      if (roundIndex + 1 < ROUNDS.length) {
        const next = roundIndex + 1;
        setRoundIndex(next);
        setGrid(buildGrid(ROUNDS[next]));
      } else {
        setFinished(true);
      }
    }, 700);
  };

  const restart = () => {
    setRoundIndex(0);
    setGrid(buildGrid(ROUNDS[0]));
    setScore(0);
    setFinished(false);
    setFeedback(null);
  };

  let result = null;
  if (finished) {
    if (score >= 5) result = { labelKey: 'colorperception_result_good_label', color: '#10B981', textKey: 'colorperception_result_good_text' };
    else if (score >= 3) result = { labelKey: 'colorperception_result_mid_label', color: '#F59E0B', textKey: 'colorperception_result_mid_text' };
    else result = { labelKey: 'colorperception_result_bad_label', color: '#EF4444', textKey: 'colorperception_result_bad_text' };
  }

  return (
    <ToolPageLayout
      title={t('colorperception_title')}
      subtitle={t('colorperception_subtitle')}
      specialty="ophtalmologue"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <Info className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('colorperception_info')}
        </p>
      </div>

      {!finished && (
        <div>
          <p className="text-sm text-[#64748B] mb-4 text-center">
            {t('colorperception_round_word')} {roundIndex + 1} / {ROUNDS.length} — {t('colorperception_instruction')}
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
            {grid.cells.map((color, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handlePick(i)}
                className="aspect-square rounded-xl transition-transform hover:scale-105 border border-black/5"
                style={{ backgroundColor: color }}
                aria-label={`case ${i + 1}`}
              />
            ))}
          </div>
          {feedback && (
            <p className={`text-center mt-4 font-medium ${feedback === 'correct' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
              {feedback === 'correct' ? t('colorperception_correct') : t('colorperception_wrong')}
            </p>
          )}
        </div>
      )}

      {finished && result && (
        <div className="text-center">
          <div className="rounded-2xl p-6 border inline-block" style={{ borderColor: `${result.color}40`, backgroundColor: `${result.color}0D` }}>
            <Palette className="w-6 h-6 mx-auto mb-2" style={{ color: result.color }} />
            <div className="text-sm text-[#64748B] mb-1">{t('colorperception_score_label')} {score} / {ROUNDS.length}</div>
            <div className="text-xl font-bold mb-2" style={{ color: result.color }}>{t(result.labelKey)}</div>
            <p className="text-sm text-[#0F172A] max-w-sm">{t(result.textKey)}</p>
          </div>
          <div>
            <button
              type="button"
              onClick={restart}
              className="mt-6 inline-flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] text-[#0F172A] text-sm font-medium px-4 py-2 rounded-xl hover:border-[#0596DE]/40"
            >
              <RotateCcw className="w-4 h-4" /> {t('colorperception_restart')}
            </button>
          </div>
        </div>
      )}

      <p className="mt-8 text-sm text-[#64748B]">
        {t('colorperception_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default ColorPerceptionCheck;
